// Verbatim port of the drop worker's PRICING + formatOrderSummary (drop/worker.js
// ~4509 / ~4535). Produces the same live estimate the real form shows. This is
// an ESTIMATE for the summary screen only — the authoritative invoice is built
// server-side by the drop worker.

import type { DropFormState } from "./dropState";

export const PRICING = {
  // Base prices (develop + scan)
  color35: 10,
  color120: 13,
  bw35: 15,
  bw120: 15,
  // Develop only prices
  developOnly: { color35: 7, color120: 8, bw35: 10, bw120: 10 } as Record<string, number>,
  // Add-ons (per roll)
  maxRes: 3,
  tiff: 2,
  contactSheets: 2,
  gallery: 6,
  pushPull: 2,
  // Per order
  mailBackNegatives: 6, // drop-off
  mailBackNegativesMailIn: 8, // mail-in
  // Prints
  printsPerRoll: 16.56, // $0.46 × 36 prints
};

const FILM_LABELS: Record<string, string> = {
  color35: "Color 35mm",
  color120: "Color 120",
  bw35: "B+W 35mm",
  bw120: "B+W 120",
};
const FILM_TYPES = ["color35", "color120", "bw35", "bw120"] as const;

export interface SummaryRow {
  label: string;
  price: string; // formatted, or ""
  muted?: boolean; // grey italic ("Included" / "—")
  accent?: boolean; // navy priced
  perk?: boolean; // green club perk
}
export interface SummaryGroup {
  title: string;
  rows: SummaryRow[];
}
export interface SummaryResult {
  groups: SummaryGroup[];
  total: number;
  note: string;
}

const n = (s: string) => parseInt(s || "0", 10) || 0;

// isMailIn is false for the portal (drop-off only) — kept for parity.
export function computeSummary(fd: DropFormState, isClubMember: boolean, isMailIn = false): SummaryResult {
  const groups: SummaryGroup[] = [];
  let total = 0;

  groups.push({
    title: "Customer Information",
    rows: [{ label: `Name: ${fd.customer_name}\nEmail: ${fd.customer_email}`, price: "" }],
  });

  const isDevelopOnly = fd.scan_size === "none";
  let totalRolls = 0;
  const filmRows: SummaryRow[] = [];
  for (const type of FILM_TYPES) {
    const count = n(fd[`${type}_count` as keyof DropFormState] as string);
    const notes = (fd[`${type}_notes` as keyof DropFormState] as string) || "";
    if (count > 0) {
      totalRolls += count;
      const basePrice = isDevelopOnly ? PRICING.developOnly[type] : (PRICING as unknown as Record<string, number>)[type];
      const lineTotal = basePrice * count;
      total += lineTotal;
      filmRows.push({
        label: `${FILM_LABELS[type]}: ${count} roll${count > 1 ? "s" : ""}${notes ? ` (Note: ${notes})` : ""}`,
        price: `${count} × $${basePrice} = $${lineTotal.toFixed(2)}`,
        accent: true,
      });
    }
  }
  const pp = fd.pushPull.filter((r) => r.filmType.trim() && r.stops);
  if (pp.length > 0) {
    const ppTotal = pp.length * PRICING.pushPull;
    total += ppTotal;
    const details = pp.map((r) => `${r.filmType} ${n(r.stops) > 0 ? "+" : ""}${r.stops}`).join(", ");
    filmRows.push({
      label: `Push/Pull: ${details}`,
      price: `${pp.length} × $${PRICING.pushPull} = $${ppTotal.toFixed(2)}`,
      accent: true,
    });
  }
  groups.push({ title: "Film Rolls", rows: filmRows });

  // Scan options
  const scanRows: SummaryRow[] = [];
  if (!isDevelopOnly) {
    const scanSizeLabel =
      fd.scan_size === "small" ? "Small" : fd.scan_size === "high_res" ? "High Res" : fd.scan_size === "max_res" ? "Max Res" : "Not selected";
    if (fd.scan_size === "max_res") {
      const t = totalRolls * PRICING.maxRes;
      total += t;
      scanRows.push({ label: `Scan Size: ${scanSizeLabel}`, price: `${totalRolls} × $${PRICING.maxRes} = $${t.toFixed(2)}`, accent: true });
    } else {
      scanRows.push({ label: `Scan Size: ${scanSizeLabel}`, price: "Included", muted: true });
    }
    const formatLabel = (fd.file_format || "jpg").toUpperCase();
    if (fd.file_format === "tiff") {
      const t = totalRolls * PRICING.tiff;
      total += t;
      scanRows.push({ label: `File Format: ${formatLabel}`, price: `${totalRolls} × $${PRICING.tiff} = $${t.toFixed(2)}`, accent: true });
    } else {
      scanRows.push({ label: `File Format: ${formatLabel}`, price: "Included", muted: true });
    }
    if (fd.contact_sheets) {
      const t = totalRolls * PRICING.contactSheets;
      total += t;
      scanRows.push({ label: "Digital Contact Sheets", price: `${totalRolls} × $${PRICING.contactSheets} = $${t.toFixed(2)}`, accent: true });
    }
    if (fd.scan_delivery === "gallery" && !isClubMember) {
      const t = totalRolls * PRICING.gallery;
      total += t;
      scanRows.push({ label: "Online Gallery", price: `${totalRolls} × $${PRICING.gallery} = $${t.toFixed(2)}`, accent: true });
    } else if (fd.scan_delivery === "gallery" && isClubMember) {
      scanRows.push({ label: "Online Gallery", price: "Club perk ✓", perk: true });
    } else if (fd.scan_delivery === "email") {
      scanRows.push({ label: "Delivery: Email (zip file)", price: "Included", muted: true });
    }
  } else {
    scanRows.push({ label: "Develop Only (no scans)", price: "—", muted: true });
  }
  groups.push({ title: "Scan Options", rows: scanRows });

  // Prints
  let printsRow: SummaryRow;
  if (fd.prints === "none" || !fd.prints) {
    printsRow = { label: "No prints requested", price: "—", muted: true };
  } else {
    const printsRolls = fd.prints === "all" ? totalRolls : Math.ceil(totalRolls / 2);
    const t = printsRolls * PRICING.printsPerRoll;
    total += t;
    const label = fd.prints === "all" ? "Prints for all rolls" : "Prints for selected rolls";
    const price = fd.prints === "all" ? `${printsRolls} × $${PRICING.printsPerRoll.toFixed(2)} = $${t.toFixed(2)}` : `~$${t.toFixed(2)}`;
    printsRow = { label, price, accent: true };
  }
  groups.push({ title: "Prints", rows: [printsRow] });

  // Negatives
  let negLabel = "";
  let negRow: SummaryRow;
  if (fd.negative_handling === "dispose") {
    negRow = { label: "Dispose of negatives", price: "—", muted: true };
  } else if (fd.negative_handling === "return") {
    const price = isMailIn ? PRICING.mailBackNegativesMailIn : PRICING.mailBackNegatives;
    total += price;
    negRow = { label: "Mail back negatives (cut & sleeved)", price: `$${price.toFixed(2)}`, accent: true };
  } else if (fd.negative_handling === "pickup") {
    const fmt = fd.pickup_format === "cut" ? "cut & sleeved" : "uncut";
    negRow = { label: `Pickup negatives (${fmt})`, price: "—", muted: true };
  } else {
    negRow = { label: negLabel || "Not selected", price: "—", muted: true };
  }
  groups.push({ title: "Negative Handling", rows: [negRow] });

  if (fd.additional_notes) {
    groups.push({ title: "Additional Notes", rows: [{ label: fd.additional_notes, price: "" }] });
  }

  const note = `${fd.prints === "some" ? "Prints estimated for half of rolls. " : ""}Final price may vary.`;
  return { groups, total, note };
}
